/* eslint-disable */
// src/pages/LoginPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { login } from "../services/authService";
import { db } from "../services/firebase";
import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { genOtp, toE164KR, inTestRange, sendOtp } from "../services/smsService";
import { useDriverStatusContext } from "context/DriverStatusContext";

/* ===========================
   공통: 전화번호 + OTP 모달 (원래 방식)
   =========================== */

const OTP_TTL_SEC = 180;


function clearClientAllStorageAndCookies() {
  if (typeof window === "undefined") return;

  try {
    // ✅ 로컬/세션 스토리지 전체 초기화
    window.localStorage?.clear?.();
    window.sessionStorage?.clear?.();
  } catch (e) { }

  try {
    // ✅ JS로 접근 가능한 쿠키 초기화(httponly는 못 지움)
    const cookies = document.cookie ? document.cookie.split(";") : [];
    cookies.forEach((c) => {
      const eqPos = c.indexOf("=");
      const name = eqPos > -1 ? c.substring(0, eqPos).trim() : c.trim();
      if (!name) return;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
  } catch (e) { }
}


function PhoneOtpDialog({ open, title, onClose, onVerified, expectedPhoneE164 }) {
  const [step, setStep] = useState("input"); // input | otp
  const [digits, setDigits] = useState(""); // 010-1234-5678 형식
  const [e164, setE164] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [devCode, setDevCode] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep("input");
    setDigits("");
    setE164("");
    setOtpInput("");
    setDevCode("");
    setSecondsLeft(0);
    setSending(false);
    setVerifying(false);
    setSent(false);
    setErr("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [open, secondsLeft]);

  const fmtKR = (v = "") => {
    const d = v.replace(/[^\d]/g, "");
    if (d.length <= 3) return d;
    if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7, 11)}`;
  };

  const cleanDigits = (v = "") => v.replace(/[^\d]/g, "");
  const canSend = cleanDigits(digits).length >= 10 && secondsLeft === 0 && !sending && !verifying;

  const onSend = async (e) => {
    e?.preventDefault?.();
    if (!canSend) return;

    const expected = String(expectedPhoneE164 || "").trim();
    const inputE164 = toE164KR(digits);

    // 테스트를 위해 잠시 주석

    if (expected && inputE164 && inputE164 !== expected) {
      alert("등록된 전화번호로 인증받으세요.");

      // ✅ 전부 초기화 후 초기화면으로 이동
      try {
        clearClientAllStorageAndCookies();
      } catch (e) { }

      // 모달 닫고, 완전 리로드로 상태도 초기화
      try {
        onClose?.();
      } catch (e) { }

      // ✅ “초기 화면” 라우트로 맞춰서 변경 가능
      // - 현재 LoginPage면 /login 또는 / 로 맞추면 됨
      window.location.replace("/login");
      return;
    }

    try {
      setSending(true);
      setErr("");

      const otp = genOtp();
      const e164Phone = toE164KR(digits);
      const isDev = inTestRange(digits);

      setDevCode(otp);
      setE164(e164Phone);
      setSecondsLeft(OTP_TTL_SEC);
      setStep("otp");
      setSent(true);
      setOtpInput("");

      if (isDev) {
        console.log("[OTP] dev range, code:", otp);
        return;
      }

      const res = await sendOtp(digits, otp);
      if (!res?.ok) {
        console.error("[OTP] sendOtp failed", res);
        setErr("인증 코드를 전송하지 못했습니다. 잠시 후 다시 시도해 주세요.");
      }
    } catch (e2) {
      console.error("[OTP] send error", e2);
      setErr("코드 전송 중 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      setSending(false);
    }
  };

  const onVerify = async (e) => {
    e?.preventDefault?.();
    if (verifying) return;

    setErr("");

    const code = (otpInput || "").replace(/\D/g, "");
    if (!sent) return setErr("먼저 인증 코드를 전송해 주세요.");
    if (code.length !== 6) return setErr("6자리 인증 코드를 입력해 주세요.");
    if (secondsLeft <= 0) return setErr("인증 시간이 만료되었습니다. 다시 요청해 주세요.");
    if (code !== devCode) return setErr("인증 코드가 올바르지 않습니다.");

    try {
      setVerifying(true);
      const ret = await onVerified?.({ phoneE164: e164, digits: cleanDigits(digits) });
      if (ret === false) return;
      // ✅ 여기서 onClose 호출하지 않음
      // - 부모(LoginPage)의 handleNameOtpVerified finally에서 setNameOtpOpen(false)로 닫힘
    } catch (e2) {
      console.error("[OTP] verify callback error", e2);
      setErr(e2?.message || "인증 처리 중 오류가 발생했습니다.");
    } finally {
      setVerifying(false);
    }
  };

  if (!open) return null;

  const mm = String(Math.floor(secondsLeft / 60));
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <OtpDim role="dialog" aria-modal="true">
      <OtpCard>
        <OtpTop>
          <OtpTitle>{title}</OtpTitle>
          <OtpCloseBtn type="button" onClick={onClose} aria-label="닫기">
            ×
          </OtpCloseBtn>
        </OtpTop>

        {step === "input" && (
          <form onSubmit={onSend}>
            <OtpField>
              <OtpLabel>휴대전화 번호</OtpLabel>
              <OtpPhoneInput
                inputMode="tel"
                autoComplete="tel"
                placeholder="010-1234-5678"
                value={digits}
                onChange={(e) => setDigits(fmtKR(e.target.value))}
              />
            </OtpField>
            {err && <OtpErr>{err}</OtpErr>}
            <OtpPrimary type="submit" disabled={!canSend}>
              {sending ? "전송 중..." : secondsLeft > 0 ? `재전송 (${mm}:{ss})` : "인증코드 전송"}
            </OtpPrimary>
            <OtpSub>알림톡(또는 SMS)으로 6자리 인증코드가 전송됩니다. (3분 유효)</OtpSub>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={onVerify}>
            <OtpField>
              <OtpLabel>인증코드</OtpLabel>
              <OtpCodeInput
                inputMode="numeric"
                maxLength={6}
                placeholder="6자리"
                value={otpInput}
                onChange={(e) => setOtpInput(e.target.value.replace(/[^\d]/g, ""))}
                autoFocus
              />
              <OtpTimer>
                남은 시간 <span>{mm}:{ss}</span>
              </OtpTimer>
            </OtpField>
            {err && <OtpErr>{err}</OtpErr>}
            <OtpPrimary
              type="submit"
              disabled={verifying || otpInput.replace(/\D/g, "").length !== 6 || secondsLeft <= 0}
            >
              {verifying ? "확인 중..." : "다음으로"}
            </OtpPrimary>
            <OtpSub>인증 완료 시 다음 단계로 진행됩니다.</OtpSub>
          </form>
        )}
      </OtpCard>
    </OtpDim>
  );
}

/* ===========================
   Firestore Helpers
   =========================== */


function clearLoginRelatedStorageOnly() {
  try {
    // ✅ localStorage: dhm. 프리픽스 전부 제거 (남은 키로 복원되는 케이스 방지)
    const ls = window.localStorage;
    const toRemove = [];
    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i);
      if (k && k.startsWith("dhm.")) toRemove.push(k);
    }
    toRemove.forEach((k) => ls.removeItem(k));
  } catch (e) { }

  try {
    // ✅ sessionStorage도 같이 정리
    window.sessionStorage?.clear?.();
  } catch (e) { }

  try {
    // ✅ 쿠키 정리 (같은 도메인/경로 기준)
    document.cookie.split(";").forEach((c) => {
      const eqPos = c.indexOf("=");
      const name = (eqPos > -1 ? c.substr(0, eqPos) : c).trim();
      if (!name) return;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  } catch (e) { }
}

async function clearIndexedDbSafe() {
  try {
    if (!window.indexedDB?.databases) return;

    const dbs = await window.indexedDB.databases();
    await Promise.all(
      (dbs || []).map((d) => {
        if (!d?.name) return Promise.resolve();
        return new Promise((resolve) => {
          const req = window.indexedDB.deleteDatabase(d.name);
          req.onsuccess = () => resolve();
          req.onerror = () => resolve();
          req.onblocked = () => resolve();
        });
      })
    );
  } catch (e) { }
}

async function hardResetToLogin() {
  try {
    clearLoginRelatedStorageOnly();
  } catch (e) { }

  try {
    setNameOtpOpen(false);
    setPendingLoginResult(null);
  } catch (e) { }

  try {
    // ✅ Firestore/캐시가 “살아있는 정보”의 1순위라 이거까지 제거
    await clearIndexedDbSafe();
  } catch (e) { }

  // ✅ 캐시된 /login을 그대로 띄우는 케이스 방지용 파라미터
  window.location.replace(`/login?reset=${Date.now()}`);
}


// function clearLoginRelatedStorageOnly() {
//   try {
//     window.localStorage.removeItem("dhm.session");
//     window.localStorage.removeItem("dhm.boundName");
//     window.localStorage.removeItem("dhm.pendingFcmToken");
//   } catch (e) { }
// }

// function hardResetToLogin() {
//   try {
//     clearLoginRelatedStorageOnly();
//   } catch (e) { }

//   try {
//     setNameOtpOpen(false);
//     setPendingLoginResult(null);
//   } catch (e) { }

//   window.location.replace("/login");
// }


async function findUsernameByPhoneE164(phoneE164) {
  const colRef = collection(db, "members");
  const q1 = query(colRef, where("phoneE164", "==", phoneE164), limit(1));
  const snap = await getDocs(q1);
  if (snap.empty) return null;
  const data = snap.docs[0].data();
  return data.username || null;
}

async function updatePasswordByPhoneE164(phoneE164, newPassword) {
  const colRef = collection(db, "members");
  const q1 = query(colRef, where("phoneE164", "==", phoneE164), limit(1));
  const snap = await getDocs(q1);
  if (snap.empty) throw new Error("해당 번호로 가입된 아이디를 찾을 수 없습니다.");
  const docRef = snap.docs[0].ref;
  await updateDoc(docRef, { password: newPassword });
}

/* ===========================
   Name Binding (로컬 바인딩)
   =========================== */

const BOUND_NAME_LS = "dhm.boundName";

function getBoundName() {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(BOUND_NAME_LS) || "";
  } catch (e) {
    return "";
  }
}

function setBoundName(name) {
  if (typeof window === "undefined") return;
  try {
    if (!name) return;
    window.localStorage.setItem(BOUND_NAME_LS, String(name));
  } catch (e) { }
}

/* ===========================
   FCM Pending Token Flush
   =========================== */

const PENDING_FCM_LS = "dhm.pendingFcmToken";

function readPendingFcmToken() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(PENDING_FCM_LS);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const token = String(parsed?.token || "").trim();
    const platform = String(parsed?.platform || "").trim();
    if (!token) return null;
    return { token, platform };
  } catch (e) {
    return null;
  }
}

function clearPendingFcmToken() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(PENDING_FCM_LS);
  } catch (e) { }
}

async function flushPendingFcmTokenIfAny({ phoneE164 }) {
  const p = String(phoneE164 || "").trim();
  if (!p) return;

  const pending = readPendingFcmToken();
  if (!pending?.token) return;

  const userRef = doc(db, "users", p);

  try {
    await setDoc(
      userRef,
      {
        phoneE164: p,
        updatedAt: serverTimestamp(),
        fcmTokens: arrayUnion(pending.token),
        fcmLastPlatform: pending.platform || null,
        fcmLastSeenAt: serverTimestamp(),
      },
      { merge: true }
    );

    clearPendingFcmToken();
    console.log("[LoginPage] flushPendingFcmToken ok", { phoneE164: p, platform: pending.platform });
  } catch (e) {
    console.warn("[LoginPage] flushPendingFcmToken failed", e);
  }
}

/* ===========================
   LoginPage 본문
   =========================== */

export default function LoginPage() {
  const nav = useNavigate();
  const { refresh } = useDriverStatusContext();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const [findIdOpen, setFindIdOpen] = useState(false);
  const [resetPwOpen, setResetPwOpen] = useState(false);

  const [idResult, setIdResult] = useState("");
  const [idResultOpen, setIdResultOpen] = useState(false);

  const [pwPhone, setPwPhone] = useState("");
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwNew, setPwNew] = useState("");
  const [pwNewConfirm, setPwNewConfirm] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [showPwNew, setShowPwNew] = useState(false);
  const [showPwNewConfirm, setShowPwNewConfirm] = useState(false);

  const [nameOtpOpen, setNameOtpOpen] = useState(false);
  const [pendingLoginResult, setPendingLoginResult] = useState(null);

  const handleBack = () => nav(-1);

  const normalizeE164 = (v) => {
    const s = String(v || "").trim();
    if (!s) return "";
    if (s.startsWith("+")) return s;
    const digits = s.replace(/\D/g, "");
    if (digits.startsWith("0")) return toE164KR(digits);
    if (digits.startsWith("82")) return `+${digits}`;
    return s;
  };

  const saveSessionForUi = (payload) => {
    if (typeof window === "undefined") return;

    try {
      const r = payload || {};
      const phoneE164 = normalizeE164(r.phoneE164 || r.phone || "");
      const name = r.name || "";
      const memberType = r.memberType || r.type || r.member_role || null;

      const session = {
        auth: "member",
        username: r.username || username,
        phoneE164: phoneE164 || null,
        name,
        memberType,
        raw: r,
        savedAt: Date.now(),
      };

      window.localStorage.setItem("dhm.session", JSON.stringify(session));
      console.log("[LoginPage] dhm.session saved", session);
    } catch (e) {
      console.error("[LoginPage] failed to save dhm.session", e);
    }
  };

  const finalizeLogin = async (result) => {
    saveSessionForUi(result || {});

    try {
      const phone = result?.phoneE164 || result?.phone;
      await flushPendingFcmTokenIfAny({ phoneE164: phone });
    } catch (eFlush) {
      console.warn("[LoginPage] flushPendingFcmToken failed", eFlush);
    }

    try {
      refresh();
    } catch (eRefresh) {
      console.warn("[LoginPage] driverStatus refresh failed", eRefresh);
    }

    nav("/home", { replace: true });
  };

  const serverPhoneE164ForPending = useMemo(() => {
    const p = pendingLoginResult?.phoneE164 || pendingLoginResult?.phone || "";
    return String(p || "").trim();
  }, [pendingLoginResult]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setErr("");

    try {
      setLoading(true);

      const result = await login({ username, password });

      const serverName = String(result?.name || "").trim();
      const boundName = String(getBoundName() || "").trim();

      if (!serverName) {
        setErr("회원 정보가 올바르지 않습니다. 관리자에게 문의해 주세요.");
        return;
      }

      const needsOtp = !boundName || boundName !== serverName;

      if (needsOtp) {
        setPendingLoginResult(result || {});
        setNameOtpOpen(true);
        return;
      }

      await finalizeLogin(result || {});
    } catch (e2) {
      console.error("[LoginPage] login error", e2);
      setErr(e2?.message || "로그인에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  };

  // const handleNameOtpVerified = async ({ phoneE164 }) => {
  //   const verified = String(phoneE164 || "").trim();
  //   const expected = String(serverPhoneE164ForPending || "").trim();

  //   if (expected && verified && verified !== expected) {
  //     setErr("등록된 전화번호로 인증받으세요.");
  //     return false;
  //   }

  //   try {
  //     setLoading(true);
  //     setErr("");

  //     const serverName = String(pendingLoginResult?.name || "").trim();
  //     if (serverName) setBoundName(serverName);

  //     await finalizeLogin(pendingLoginResult || {});
  //     return true;
  //   } catch (e3) {
  //     console.error("[LoginPage] name otp finalize failed", e3);
  //     setErr(e3?.message || "인증 후 로그인에 실패했습니다.");
  //     return false;
  //   } finally {
  //     setLoading(false);
  //     setNameOtpOpen(false);
  //     setPendingLoginResult(null);
  //   }
  // };


  const handleNameOtpVerified = async ({ phoneE164 }) => {
    const verified = String(phoneE164 || "").trim();
    const expected = String(serverPhoneE164ForPending || "").trim();


    // 테스트를 위해 잠시 주석
    if (expected && verified && verified !== expected) {
      alert("등록된 전화번호로 인증받으세요.");

      // ✅ 표적 초기화(권장: 전체 clear 말고 세션/바인딩만)
      try {
        window.localStorage.removeItem("dhm.session");
        window.localStorage.removeItem("dhm.boundName");
        window.localStorage.removeItem("dhm.pendingFcmToken");
      } catch (e) { }

      // ✅ 모달/대기상태 정리
      try {
        setNameOtpOpen(false);
        setPendingLoginResult(null);
      } catch (e) { }

      // ✅ 라우팅 스택 제거 + 완전 초기화
      window.location.replace("/login");
      return false;
    }

    try {
      setLoading(true);
      setErr("");

      const serverName = String(pendingLoginResult?.name || "").trim();
      if (serverName) setBoundName(serverName);

      await finalizeLogin(pendingLoginResult || {});
      return true;
    } catch (e3) {
      console.error("[LoginPage] name otp finalize failed", e3);
      setErr(e3?.message || "인증 후 로그인에 실패했습니다.");
      return false;
    } finally {
      setLoading(false);
      setNameOtpOpen(false);
      setPendingLoginResult(null);
    }
  };

  
  const handleIdVerified = async ({ phoneE164 }) => {
    try {
      const found = await findUsernameByPhoneE164(phoneE164);
      if (!found) setIdResult("해당 번호로 등록된 아이디를 찾을 수 없습니다.");
      else setIdResult(`회원님의 아이디는 "${found}" 입니다.`);
      setIdResultOpen(true);
    } catch (e2) {
      console.error(e2);
      setIdResult("아이디 조회 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
      setIdResultOpen(true);
    }
  };

  const handlePwVerified = ({ phoneE164 }) => {
    setPwPhone(phoneE164);
    setPwNew("");
    setPwNewConfirm("");
    setPwErr("");
    setShowPwNew(false);
    setShowPwNewConfirm(false);
    setPwModalOpen(true);
  };

  const submitNewPassword = async (e) => {
    e?.preventDefault?.();
    if (pwSaving) return;
    setPwErr("");

    const n1 = pwNew.trim();
    const n2 = pwNewConfirm.trim();

    if (!n1 || !n2) return setPwErr("새 비밀번호를 모두 입력해 주세요.");
    if (n1.length < 6) return setPwErr("비밀번호는 6자 이상이어야 합니다.");
    if (n1 !== n2) return setPwErr("새 비밀번호와 확인이 일치하지 않습니다.");

    try {
      setPwSaving(true);
      await updatePasswordByPhoneE164(pwPhone, n1);
      alert("비밀번호가 변경되었습니다. 변경된 비밀번호로 다시 로그인해 주세요.");
      setPwSaving(false);
      setPwModalOpen(false);
    } catch (e2) {
      console.error(e2);
      setPwErr(e2?.message || "비밀번호 변경 중 오류가 발생했습니다.");
      setPwSaving(false);
    }
  };

  return (
    <Wrap>
      <Card as="form" onSubmit={onSubmit}>
        <HeaderRow>
          <BackButton type="button" onClick={handleBack}>
            <span className="arrow">←</span>
            <span className="label">뒤로</span>
          </BackButton>
        </HeaderRow>

        <Title>로그인</Title>

        <Field>
          <label>아이디</label>
          <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="아이디" autoFocus />
        </Field>

        <Field>
          <label>비밀번호</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="비밀번호" />
        </Field>

        <Helper>
          <span>아이디나 비밀번호를 잊으셨나요? </span>
          <LinkButton type="button" onClick={() => setFindIdOpen(true)}>
            아이디
          </LinkButton>
          <span> / </span>
          <LinkButton type="button" onClick={() => setResetPwOpen(true)}>
            비밀번호
          </LinkButton>
        </Helper>

        {err && <ErrorMsg>{err}</ErrorMsg>}

        <Button type="submit" disabled={loading}>
          {loading ? "로그인 중..." : "로그인"}
        </Button>

        <Hint>
          처음이신가요? <a href="/auth/phone">휴대폰 인증 후 회원가입</a>
        </Hint>
      </Card>

      <PhoneOtpDialog
        open={nameOtpOpen}
        title="추가 인증이 필요합니다"
        onClose={hardResetToLogin}
        onVerified={handleNameOtpVerified}
        expectedPhoneE164={serverPhoneE164ForPending}
      />

      <PhoneOtpDialog
        open={findIdOpen}
        title="아이디 찾기 - 전화번호 인증"
        onClose={() => setFindIdOpen(false)}
        onVerified={handleIdVerified}
      />

      <PhoneOtpDialog
        open={resetPwOpen}
        title="비밀번호 재설정 - 전화번호 인증"
        onClose={() => setResetPwOpen(false)}
        onVerified={handlePwVerified}
      />

      {idResultOpen && (
        <SimpleDim role="dialog" aria-modal="true" onClick={() => setIdResultOpen(false)}>
          <SimpleCard onClick={(e) => e.stopPropagation()}>
            <SimpleTitle>아이디 찾기 결과</SimpleTitle>
            <SimpleBody>{idResult}</SimpleBody>
            <SimpleButton type="button" onClick={() => setIdResultOpen(false)}>
              확인
            </SimpleButton>
          </SimpleCard>
        </SimpleDim>
      )}

      {pwModalOpen && (
        <SimpleDim role="dialog" aria-modal="true" onClick={() => (!pwSaving ? setPwModalOpen(false) : null)}>
          <SimpleCard onClick={(e) => e.stopPropagation()}>
            <SimpleTitle>비밀번호 재설정</SimpleTitle>
            <SimpleBody>
              <p style={{ fontSize: 13, marginTop: 0, marginBottom: 10 }}>새 비밀번호를 입력해 주세요.</p>

              <PwField>
                <label>새 비밀번호</label>
                <PwInputWrap>
                  <PwInput
                    type={showPwNew ? "text" : "password"}
                    value={pwNew}
                    onChange={(e) => setPwNew(e.target.value)}
                    placeholder="새 비밀번호 (6자 이상)"
                    disabled={pwSaving}
                  />
                  <EyeBtn type="button" onClick={() => setShowPwNew((v) => !v)} disabled={pwSaving}>
                    {showPwNew ? "숨김" : "보기"}
                  </EyeBtn>
                </PwInputWrap>
              </PwField>

              <PwField>
                <label>새 비밀번호 확인</label>
                <PwInputWrap>
                  <PwInput
                    type={showPwNewConfirm ? "text" : "password"}
                    value={pwNewConfirm}
                    onChange={(e) => setPwNewConfirm(e.target.value)}
                    placeholder="새 비밀번호 재입력"
                    disabled={pwSaving}
                  />
                  <EyeBtn type="button" onClick={() => setShowPwNewConfirm((v) => !v)} disabled={pwSaving}>
                    {showPwNewConfirm ? "숨김" : "보기"}
                  </EyeBtn>
                </PwInputWrap>
              </PwField>

              {pwErr && <ErrorMsg>{pwErr}</ErrorMsg>}

              <SimpleButton type="button" onClick={submitNewPassword} disabled={pwSaving}>
                {pwSaving ? "변경 중..." : "비밀번호 변경"}
              </SimpleButton>
            </SimpleBody>
          </SimpleCard>
        </SimpleDim>
      )}
    </Wrap>
  );
}

/* ===== 스타일 ===== */

const Wrap = styled.div`
  min-height: 100dvh;
  display: grid;
  place-items: center;
  background: #fff;
`;

const Card = styled.div`
  width: min(480px, 92vw);
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 20px 20px 24px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #e0e0e0;
  background: #fafafa;
  font-size: 13px;
  cursor: pointer;

  .arrow {
    font-size: 14px;
  }
  .label {
    line-height: 1;
  }
`;

const Title = styled.h2`
  margin: 0 0 16px;
  font-size: 22px;
`;

const Field = styled.div`
  display: grid;
  gap: 6px;
  margin-bottom: 14px;

  label {
    font-size: 13px;
    color: #666;
  }

  input {
    height: 46px;
    border-radius: 12px;
    border: 1px solid #ddd;
    padding: 0 12px;
    font-size: 15px;
  }
`;

const ErrorMsg = styled.div`
  color: #c00;
  font-size: 13px;
  margin: 6px 0 4px;
`;

const Button = styled.button`
  width: 100%;
  height: 50px;
  border-radius: 12px;
  font-size: 16px;
  color: #fff;
  background: #3479d6;
  border: none;
`;

const Hint = styled.p`
  margin: 14px 0 0;
  font-size: 13px;
  color: #666;

  a {
    color: #3479d6;
  }
`;

const Helper = styled.div`
  margin-top: -4px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #666;
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 0 2px;
  color: #3479d6;
  text-decoration: underline;
  font-size: 13px;
  cursor: pointer;
`;

/* ===== 공용 심플 모달 ===== */

const SimpleDim = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
`;

const SimpleCard = styled.div`
  width: min(360px, 90vw);
  background: #fff;
  border-radius: 16px;
  padding: 20px 18px 18px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SimpleTitle = styled.h3`
  margin: 0;
  font-size: 18px;
`;

const SimpleBody = styled.div`
  font-size: 14px;
  color: #333;
  line-height: 1.5;
`;

const SimpleButton = styled.button`
  margin-top: 8px;
  height: 44px;
  border-radius: 10px;
  border: none;
  background: #3479d6;
  color: #fff;
  font-size: 15px;
  cursor: pointer;
`;

/* ===== 비밀번호 재설정 입력 ===== */

const PwField = styled.div`
  display: grid;
  gap: 6px;
  margin-bottom: 12px;

  label {
    font-size: 13px;
    color: #444;
  }
`;

const PwInputWrap = styled.div`
  position: relative;
`;

const PwInput = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 0 40px 0 12px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #4f46e5;
    box-shadow: 0 0 0 1px rgba(79, 70, 229, 0.15);
  }
`;

const EyeBtn = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  font-size: 12px;
  color: #64748b;
  cursor: pointer;
`;

/* ===== OTP 모달 스타일 ===== */

const OtpDim = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
`;

const OtpCard = styled.section`
  width: min(520px, 100%);
  background: #fff;
  color: #111;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  padding: 18px 16px;
`;

const OtpTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const OtpTitle = styled.h3`
  margin: 0;
  font-size: 18px;
`;

const OtpCloseBtn = styled.button`
  border: 1px solid #e6e0d8;
  background: #fff;
  border-radius: 10px;
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  cursor: pointer;
`;

const OtpField = styled.label`
  display: grid;
  gap: 6px;
  margin: 12px 0 8px;
  font-size: 13px;
`;

const OtpLabel = styled.span`
  color: #333;
`;

const OtpPhoneInput = styled.input`
  height: 48px;
  border-radius: 12px;
  border: 1px solid #e6e0d8;
  padding: 0 14px;
  font-size: 16px;
  text-align: center;
  letter-spacing: 1px;
  width: 100%;
box-sizing: border-box;
max-width: 100%;
`;

const OtpCodeInput = styled(OtpPhoneInput)`
  letter-spacing: 6px;
`;

const OtpTimer = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #666;

  span {
    margin-left: 6px;
  }
`;

const OtpErr = styled.div`
  color: #e03131;
  font-size: 12px;
  margin-top: 4px;
`;

const OtpPrimary = styled.button`
  height: 46px;
  width: 100%;
  border: none;
  border-radius: 12px;
  background: #3479d6;
  color: #fff;
  font-size: 15px;
  margin-top: 8px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const OtpSub = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  color: #777;
`;
